import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@vinoga/db";
import { Plus, Pencil, Trash2, Image as ImageIcon, ExternalLink, GripVertical } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";
import { mediaSrc } from "@/lib/media";

// Known placements that the storefront actually renders. Free-text is
// still allowed via the editor so new placements don't require a code
// change here.
const PLACEMENTS = [
  { key: "home", label: "Sākumlapa" },
  { key: "home_secondary", label: "Sākumlapa (sekundārā)" },
  { key: "category_top", label: "Kategorija — augšā" },
];

type BannerRow = {
  id: string;
  title: string;
  image_url: string;
  link_url: string | null;
  alt_text: string | null;
  placement: string;
  sort_order: number;
  is_active: boolean;
  date_from: string | null;
  date_to: string | null;
  desktop_span: number;
};

export function Banners() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const tr = useT();
  const [activeTab, setActiveTab] = useState<string>(PLACEMENTS[0]!.key);
  const [rows, setRows] = useState<BannerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const refresh = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("banners")
      .select("*")
      .eq("placement", activeTab)
      .order("sort_order", { ascending: true });
    if (error) {
      toast({
        title: tr("banners.errorLoading", "Failed to load banners"),
        description: error.message,
        variant: "destructive",
      });
    } else {
      setRows((data ?? []) as BannerRow[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const remove = async (id: string) => {
    if (!confirm(tr("banners.confirmDelete", "Delete this banner?"))) return;
    const { error } = await supabase.from("banners").delete().eq("id", id);
    if (error) {
      toast({ title: tr("common.deleteFailed", "Delete failed"), variant: "destructive" });
      return;
    }
    refresh();
  };

  const handleDragStart = (index: number) => setDragIndex(index);
  const handleDragOver = useCallback(
    (e: React.DragEvent, overIndex: number) => {
      e.preventDefault();
      if (dragIndex === null || dragIndex === overIndex) return;
      const reordered = [...rows];
      const [moved] = reordered.splice(dragIndex, 1);
      reordered.splice(overIndex, 0, moved!);
      setRows(reordered);
      setDragIndex(overIndex);
    },
    [dragIndex, rows]
  );
  const handleDragEnd = async () => {
    setDragIndex(null);
    setSavingOrder(true);
    try {
      await Promise.all(
        rows.map((b, idx) =>
          supabase.from("banners").update({ sort_order: idx }).eq("id", b.id)
        )
      );
      toast({ title: tr("common.saved", "Order saved") });
    } catch {
      toast({
        title: tr("banners.orderFailed", "Failed to save order"),
        variant: "destructive",
      });
    } finally {
      setSavingOrder(false);
    }
  };

  const fmt = (d: string | null) =>
    d
      ? new Date(d).toLocaleDateString("lv-LV", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-xl font-bold text-foreground">
          {tr("banners.title", "Banners")}
        </h1>
        <Button size="sm" onClick={() => navigate(`/banners/new?placement=${activeTab}`)}>
          <Plus size={16} /> {tr("banners.add", "Add banner")}
        </Button>
      </div>

      <div className="flex gap-1 border-b border-border">
        {PLACEMENTS.map((p) => (
          <button
            key={p.key}
            onClick={() => setActiveTab(p.key)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === p.key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {savingOrder && (
        <p className="animate-pulse text-xs text-muted-foreground">
          {tr("banners.savingOrder", "Saving order…")}
        </p>
      )}

      {loading ? (
        <div className="p-4 font-body text-sm text-muted-foreground">
          {tr("common.loading", "Loading…")}
        </div>
      ) : rows.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">
          <ImageIcon size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-body text-sm">
            {tr("banners.empty", "No banners yet for this placement.")}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            {tr("banners.dragHint", "Drag rows to reorder. Order saves automatically.")}
          </p>
          <div className="overflow-hidden rounded-lg border border-border bg-card">
            {rows.map((b, idx) => (
              <div
                key={b.id}
                draggable
                onDragStart={() => handleDragStart(idx)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDragEnd={handleDragEnd}
                className={`flex cursor-grab items-center gap-3 border-b border-border px-4 py-3 transition-colors last:border-b-0 active:cursor-grabbing ${
                  dragIndex === idx ? "bg-muted/50" : "hover:bg-muted/20"
                }`}
              >
                <GripVertical size={16} className="shrink-0 text-muted-foreground/40" />
                <div className="flex h-12 w-20 shrink-0 items-center justify-center overflow-hidden rounded border border-border bg-muted/20">
                  {b.image_url ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={mediaSrc(b.image_url) ?? undefined}
                      alt={b.alt_text || b.title}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <ImageIcon size={14} className="text-muted-foreground/40" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{b.title}</p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                      {b.desktop_span} col
                    </span>
                    {b.link_url && (
                      <a
                        href={b.link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex max-w-[260px] items-center gap-1 truncate text-xs text-primary hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {b.link_url} <ExternalLink size={10} />
                      </a>
                    )}
                    {(b.date_from || b.date_to) && (
                      <span className="text-xs text-muted-foreground">
                        {fmt(b.date_from) ?? "∞"} → {fmt(b.date_to) ?? "∞"}
                      </span>
                    )}
                  </div>
                </div>
                <Badge variant={b.is_active ? "default" : "secondary"} className="shrink-0">
                  {b.is_active
                    ? tr("banners.statusActive", "active")
                    : tr("banners.statusInactive", "inactive")}
                </Badge>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => navigate(`/banners/${b.id}?placement=${activeTab}`)}
                  >
                    <Pencil size={14} className="text-muted-foreground" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => remove(b.id)}
                  >
                    <Trash2 size={14} className="text-muted-foreground" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
